import React, { useState } from 'react';
import { AudioLines, Loader2, Play, X } from 'lucide-react';
import { toast } from 'sonner';
import { TTS_VOICES, speak, speechErrorMessage } from '@/utils/tts';
import { useSpeech } from '@/hooks/useSpeech';

const PREVIEW = 'Hi, this is how your messages will sound when read aloud.';

// Header pill → small sheet: pick the speaking voice and opt in to hearing incoming texts read aloud.
export const VoiceSettings = ({ voice, onVoiceChange, readAloud, onReadAloudChange }) => {
  const [open, setOpen] = useState(false);
  const { loadingId, speakingId } = useSpeech();
  const previewing = loadingId === 'voice-preview' || speakingId === 'voice-preview';
  const current = TTS_VOICES.find((v) => v.id === voice) || TTS_VOICES[0];

  const preview = (id) => speak(PREVIEW, { id: 'voice-preview', voice: id }).catch((err) => toast.error(speechErrorMessage(err)));

  return (
    <div className="filter-chip-wrap voice-settings">
      <button
        type="button"
        className={`pill-toggle ${readAloud ? 'on' : 'off'}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title="Voice used to read messages aloud"
        data-testid="voice-settings-button"
      >
        <AudioLines size={15} /> Voice · {current.label}
      </button>
      {open && (
        <>
          <div className="filter-backdrop" onClick={() => setOpen(false)} data-testid="voice-settings-backdrop" />
          <div className="filter-menu voice-menu" role="dialog" aria-label="Voice settings" data-testid="voice-settings-menu">
            <div className="filter-menu-head">
              <span>Spoken voice</span>
              <button type="button" className="msg-search-clear" onClick={() => setOpen(false)} aria-label="Close" data-testid="voice-settings-close"><X size={14} /></button>
            </div>
            <div className="filter-menu-body">
              <label className="voice-toggle-row" data-testid="read-aloud-row">
                <input
                  type="checkbox"
                  checked={readAloud}
                  onChange={(e) => onReadAloudChange(e.target.checked)}
                  data-testid="read-aloud-toggle"
                />
                <span>
                  <strong>Read incoming texts aloud</strong>
                  <small>Walkie-talkie style, while this tab is open. Follows Sounds and quiet hours.</small>
                </span>
              </label>
              <div className="filter-group">Voice</div>
              {TTS_VOICES.map((v) => (
                <div key={v.id} className={`voice-option ${v.id === voice ? 'selected' : ''}`}>
                  <button type="button" className="voice-pick" onClick={() => onVoiceChange(v.id)} data-testid={`voice-option-${v.id}`}>
                    <span className="voice-name">{v.label}</span>
                    <span className="voice-hint">{v.hint}</span>
                  </button>
                  <button
                    type="button"
                    className="voice-preview"
                    onClick={() => preview(v.id)}
                    disabled={previewing}
                    aria-label={`Preview ${v.label}`}
                    data-testid={`voice-preview-${v.id}`}
                  >
                    {previewing ? <Loader2 size={13} className="spin" /> : <Play size={13} />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
